import { EntityTypes } from 'constants/entities';
import { ICreate, IUpdate } from 'core/actions/admin/data';
import { IModuleDAO } from 'types/domain/module';
import { ILanguage, IText, ITranslation } from 'types/domain/text';

const without = (ids: number[] | null | undefined, id: number) => (ids ?? []).filter(other => other !== id);
const withId = (ids: number[] | null | undefined, id: number) => [...without(ids, id), id];

/**
 * Keep a module's translation state in step with a save, so the language
 * menu's counts and the rail's per-slide marks move as the author works
 * instead of waiting for the next load.
 *
 * - A translation save (TNL) marks its text translated — or, when it was
 *   removed, not — and brings its language onto the module if this was the
 *   first text translated into it.
 * - A save of the base text leaves every translation of it behind the English,
 *   the same rule the API applies on load.
 *
 * Only flags are taken from the response, never the html: saves are debounced,
 * so by the time one lands the author may have typed on, and the editor
 * already holds the newer text.
 */
export function applyTextSave(state, arg: IUpdate | ICreate, saved: any) {
  if (!saved?.id) return;

  const module: IModuleDAO | undefined = arg.parentModuleId
    ? state[EntityTypes.MODULE]?.[arg.parentModuleId]
    : undefined;

  if (arg.type === EntityTypes.TRANSLATION) {
    const translation = saved as ITranslation;
    const translated = translation.status !== -1;
    const text = state[EntityTypes.TEXT]?.[translation.textId];

    if (text && text.languageId === translation.languageId)
      state[EntityTypes.TEXT][translation.textId] = { ...text, translated, outdated: false };

    if (!module) return;

    const languages: ILanguage[] = module.languages ?? [];
    const known = languages.some(language => language.id === translation.languageId);
    const entry = (language: ILanguage): ILanguage => ({
      ...language,
      textIds: translated
        ? withId(language.textIds, translation.textId)
        : without(language.textIds, translation.textId),
      outdatedTextIds: without(language.outdatedTextIds, translation.textId),
    });

    state[EntityTypes.MODULE][module.id] = {
      ...module,
      languages: known
        ? languages.map(language => (language.id === translation.languageId ? entry(language) : language))
        : translated
        ? // Named from the full language list when it's shown; the id is all this needs.
          [...languages, entry({ id: translation.languageId, isoCode: '', title: '' })]
        : languages,
    };

    return;
  }

  if (arg.type !== EntityTypes.TEXT) return;

  const text = saved as IText;

  if (!module?.languages?.some(language => language.textIds?.includes(text.id))) return;

  state[EntityTypes.MODULE][module.id] = {
    ...module,
    languages: module.languages.map(language =>
      language.textIds?.includes(text.id)
        ? { ...language, outdatedTextIds: withId(language.outdatedTextIds, text.id) }
        : language,
    ),
  };
}
